import { useMemo, useState } from 'react';
import { Box, Checkbox, Collapse, IconButton, Stack, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ListViewTableBodyRow from '../../../../Shared/Components/ListViewTable/ListViewTableBodyRow';
import ListViewTableBodyCell from '../../../../Shared/Components/ListViewTable/ListViewTableBodyCell';
import { MIN_COLUMN_WIDTH, TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED } from '../styles';
import { currencyFormatter } from '../../../../utils/helpers';
import { COLORS, DATE_FORMATS } from '../../../../utils/Application_Constants';
import {
    SelectionStatus,
    useInvoicingSelectionContext,
} from '../../../Contexts/InvoicingSelectionContext';
import AnimatedExpandIcon from '../../../../Shared/Components/AnimatedExpandIcon';
import useHierarchyConnectorVertical from '../hooks/useHierarchyConnectorVertical';
import { formatUtcDate } from '../../../../utils/formatDate';
import InvoicingItemContainer from './InvoicingItemContainer';
import StatusPill from '../StatusPill';

const getBookingType = (booking) => {
    const type = [];
    // if there's any normal PTS or expenses, add "General"
    if (booking.timesheets.length > 0 || booking.expenses.length > 0) {
        type.push('General');
    }
    // if any of the timesheets or expenses are marked as RT, add "RT",
    if (
        booking.timesheets.some((timesheet) => timesheet.rt) ||
        booking.expenses.some((expense) => expense.rt)
    ) {
        type.push('RT');
    }
    // if there's a deposit included, add "Deposit"
    if (booking.deposits.length > 0) {
        type.push('Deposit');
    }
    // if there's a credit memo included, add "Credit Memo"
    if (booking.creditMemos.length > 0) {
        type.push('Credit Memo');
    }
    return type.join(', ');
};

const BookingContainerCollapse = ({ booking }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const toggleExpand = () => {
        setIsExpanded((prevValue) => !prevValue);
    };
    const {
        lastChildRef,
        expandIconRef,
        hierarchyConnectorVerticalRef,
        mainContainerRef,
        renderConnector,
    } = useHierarchyConnectorVertical();

    const type = useMemo(() => getBookingType(booking), [booking]);
    const items = useMemo(
        () => [
            ...booking.timesheets.map((timesheet) => ({
                type: 'timesheet',
                id: timesheet.id,
                data: timesheet,
            })),
        ],
        [booking]
    );

    const { getBookingSelectionState, handleSelectBooking } = useInvoicingSelectionContext();
    const bookingSelectionStatus = getBookingSelectionState(booking.bookingId);

    const hasSubItems = items.length > 0;

    return (
        <Box
            ref={mainContainerRef}
            sx={
                isExpanded
                    ? { backgroundColor: '#F5F6F7', pb: '10px', borderRadius: '5px' }
                    : undefined
            }
            position="relative"
        >
            <ListViewTableBodyRow
                sx={{ cursor: hasSubItems ? 'pointer' : 'default', height: 80 }}
                onClick={hasSubItems ? toggleExpand : undefined}
                noBorder={isExpanded}
                aria-expanded={isExpanded}
                aria-controls={
                    isExpanded ? `booking-items-collapse-${booking.bookingId}` : undefined
                }
            >
                <ListViewTableBodyCell
                    sx={{
                        width: '90px',
                        pl: '16px !important',
                        pr: '2.5px !important',
                        flexGrow: '0 !important',
                        justifyContent: 'space-between',
                    }}
                >
                    {items.length > 0 && (
                        <AnimatedExpandIcon ref={expandIconRef} isExpanded={isExpanded} />
                    )}
                    {['PreInvoiced', 'Invoiced'].includes(booking.status) && (
                        <Checkbox
                            sx={{ p: 1, ml: items.length === 0 ? 'auto' : undefined }}
                            checked={bookingSelectionStatus === SelectionStatus.SELECTED}
                            indeterminate={bookingSelectionStatus === SelectionStatus.INDETERMINATE}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelectBooking(booking.bookingId);
                            }}
                        />
                    )}
                </ListViewTableBodyCell>
                <ListViewTableBodyCell
                    size={1}
                    sx={{ minWidth: MIN_COLUMN_WIDTH, ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED }}
                >
                    {booking.clientName}
                </ListViewTableBodyCell>
                <ListViewTableBodyCell
                    size={1}
                    sx={{
                        minWidth: MIN_COLUMN_WIDTH,
                        ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                        whiteSpace: 'normal',
                    }}
                >
                    {booking.customerNumber}
                </ListViewTableBodyCell>
                <ListViewTableBodyCell
                    size={1}
                    sx={{ minWidth: MIN_COLUMN_WIDTH, ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED }}
                >
                    {booking.invoiceNumber}
                </ListViewTableBodyCell>
                <ListViewTableBodyCell
                    size={1}
                    sx={{ minWidth: MIN_COLUMN_WIDTH, display: 'flex', alignItems: 'center' }}
                    className="show-overflow"
                >
                    <Stack spacing={0.5}>
                        <Typography sx={TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED}>
                            {booking.clinicianName}
                        </Typography>
                        <Typography sx={{ ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED, fontSize: 12 }}>
                            Locations: {booking.numberOfLocations}
                        </Typography>
                    </Stack>
                </ListViewTableBodyCell>
                <ListViewTableBodyCell
                    size={1}
                    sx={{ minWidth: MIN_COLUMN_WIDTH, ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED }}
                >
                    {`BKG-${booking.bookingId || 'Unknown'}`}
                </ListViewTableBodyCell>
                <ListViewTableBodyCell
                    size={1}
                    sx={{ minWidth: MIN_COLUMN_WIDTH, ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED }}
                >
                    {formatUtcDate(booking.invoiceDate, DATE_FORMATS.US_DATE)}
                </ListViewTableBodyCell>
                <ListViewTableBodyCell
                    size={1}
                    sx={{ minWidth: MIN_COLUMN_WIDTH, ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED }}
                >
                    {formatUtcDate(booking.postedDate, DATE_FORMATS.US_DATE)}
                </ListViewTableBodyCell>
                <ListViewTableBodyCell size={1} sx={{ minWidth: 130 }}>
                    {booking.status ? <StatusPill status={booking.status} /> : '-'}
                </ListViewTableBodyCell>
                <ListViewTableBodyCell
                    size={1}
                    sx={{
                        minWidth: MIN_COLUMN_WIDTH,
                        ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                        fontWeight: 600,
                    }}
                >
                    {currencyFormatter(booking.amount)}
                </ListViewTableBodyCell>
                <ListViewTableBodyCell
                    size={1}
                    sx={{ minWidth: MIN_COLUMN_WIDTH, ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED }}
                >
                    {type}
                </ListViewTableBodyCell>
                <ListViewTableBodyCell size={0.5}>
                    <IconButton onClick={(e) => e.stopPropagation()}>
                        <PictureAsPdfIcon sx={{ color: COLORS.LT_MIDNIGHT50 }} />
                    </IconButton>
                </ListViewTableBodyCell>
            </ListViewTableBodyRow>

            <Collapse in={isExpanded} id={`invoice-items-collapse-${booking.bookingId}`}>
                {renderConnector(isExpanded)}
                <Box sx={{ ml: '56px' }}>
                    {items.map((item, index) => (
                        <InvoicingItemContainer
                            key={`bkg:${booking.bookingId}|${item.type}:${item.id}`}
                            bookingId={booking.bookingId}
                            type={item.type}
                            itemId={item.id}
                            data={item.data}
                            hierarchyConnectorVerticalRef={hierarchyConnectorVerticalRef}
                            ref={index === items.length - 1 ? lastChildRef : null}
                        />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};

export default BookingContainerCollapse;
